import React, { forwardRef } from "react";
import {
  Autocomplete,
  CircularProgress,
  TextField,
  Checkbox,
  Link,
  Button,
  Paper,
  Box,
  Chip,
} from "@mui/material";
import { CheckBoxOutlineBlank, CheckBox } from "@mui/icons-material";
import theme from "../../../theme/Theme";
import ChevronDown from "../../../../public/assets/icons/chevron-down.svg";
import { isEqual } from "lodash";

const icon = <CheckBoxOutlineBlank fontSize="small" />;
const checkedIcon = <CheckBox fontSize="small" />;

let lastScrollTop = 0;

/* -------------------------------------------------------------------------- */
/*  CUSTOM LISTBOX – scroll restore + footer container                       */
/* -------------------------------------------------------------------------- */

const CustomListboxComponent = forwardRef<HTMLDivElement, any>((props, ref) => {
  const { children, handleCancel, handleSelectAll, handleClearAll, handleApply, ...other } = props;
  const scrollRef = React.useRef<HTMLDivElement | null>(null);

  React.useLayoutEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = lastScrollTop;
  });

  return (
    <Paper {...other} sx={{ width: "100%" }}>
      <Box
        ref={ref}
        sx={{
          display: "flex",
          flexDirection: "column",
          maxHeight: 330,
          overflow: "hidden",
          width: "100%",
        }}
      >
        {/* Scrollable list */}
        <Box
          ref={scrollRef}
          className="custom-autocomplete-scroll"
          sx={{
            overflowY: "auto",
            width: "100%",
          }}
        >
          <ul role="listbox" style={{ margin: 0, padding: 0, listStyle: "none" }}>{children}</ul>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            borderTop: "1px solid #ccc",
            background: "white",
            paddingTop: "6px",
          }}
        >
          {/* Select All / Clear All */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              padding: "0px 10px",
            }}
          >
            <Link component="button" underline="none" onClick={handleSelectAll} sx={{ color: "#e5927d" }}>
              Select All
            </Link>

            <Link component="button" underline="none" onClick={handleClearAll} sx={{ color: "#e5927d" }}>
              Clear All
            </Link>
          </Box>

          {/* Cancel / Apply */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", padding: "12px 10px" }}>
            <Button
              onClick={handleCancel}
              sx={{
                border: "1px solid #e5927d",
                borderRadius: "3px",
                height: "32px",
                padding: "0px 14px",
              }}
            >
              Cancel
            </Button>

            <Button
              onClick={handleApply}
              sx={{
                marginLeft: "10px",
                background: "#e5927d",
                borderRadius: "3px",
                height: "32px",
                padding: "0px 14px",
                color: "white",
                "&:hover": { backgroundColor: "#e5927d" },
              }}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
});

/* -------------------------------------------------------------------------- */
/* MAIN AUTOCOMPLETE COMPONENT                                             */
/* -------------------------------------------------------------------------- */

export default function CustomAutoCompleteCheckbox(props) {
  const { values, filterOptions, columnOptions } = props;
  const { onValueChange, searchDebounce } = props;
  const { selectedValue, selectedField, selectedFieldId, setSelectedFieldId } = props;
  const { loading, nonManagerDetail } = props;
  const { text, setText } = props;
  const { styles, disabled } = props;

  const [open, setOpen] = React.useState(false);
  const [selected, setSelected] = React.useState<any[]>([]);
  const [activeValues, setActiveValues] = React.useState<any[]>([]);

  const getOptionLabel = (o: any) => o.label || o;

  /* Sync selected */
  React.useEffect(() => {
    if (!isEqual(selectedValue, selected)) {
      setSelected(selectedValue.filter((o: any) => o.id));
    }
  }, [selectedValue]);

  React.useEffect(() => {
  setActiveValues(values);   
}, [values]);


  const handleChange = (event: any, newValue: any[]) => {
    const cleaned = newValue.filter(
      (item) =>
        item &&
        typeof item === "object" &&
        item.id &&
        item.label &&
        item.label.toString().trim() !== ""
    );

    const unique = Array.from(new Map(cleaned.map((i) => [i.id, i])).values());

    const oldCount = selected.length;
    const newCount = unique.length;

    setSelected(unique);

    // Chip removed normally
    if (newCount < oldCount) {
      onValueChange(event, unique);
      return;
    }

    // User clicked CLEAR X → newValue = []
    if (oldCount > 0 && newCount === 0) {
      onValueChange(event, []);
      return;
    }
};

  /* Select All */
  const handleSelectAll = () => {
    const ids = new Set(selected.map((s) => s.id));
    const merged = [...selected];
    values.forEach((v) => {
      if (!ids.has(v.id)) merged.push(v);
    });
    setSelected(merged);
  };

  /* Clear All */
  const handleClearAll = () => setSelected([]);

  /* Apply */
  const handleApply = (e: any) => {
    onValueChange(e, selected);
    setOpen(false);
  };

  /* Cancel */
  const handleCancel = () => {
    setSelected(selectedValue);
    setOpen(false);
  };

  return (
    <Autocomplete
    data-testid="autocomplete-root"
      multiple
      disableCloseOnSelect
      autoHighlight={false}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={activeValues}
      popupIcon={<img src={ChevronDown} alt="chevron-down" />}
      forcePopupIcon
      ListboxComponent={(listboxProps) => (
        <CustomListboxComponent
          {...listboxProps}
          handleCancel={handleCancel}
          handleSelectAll={handleSelectAll}
          handleClearAll={handleClearAll}
          handleApply={handleApply}
        />
      )}
  componentsProps={{
          popper: {
            sx: {
              "& .MuiPaper-root": {
                width: "100% !important",
                backgroundColor: "#fff",
                boxShadow:
                  "0px 3px 6px rgba(0,0,0,0.16), 0px 8px 24px rgba(0,0,0,0.23)",
                border: "none",
                overflow: "hidden",
                padding: 0, 
              },

              "& .MuiAutocomplete-listbox": {
                padding: 0,
                margin: 0,
                width: "100% !important",
              },
            },
          },
      }}
      getOptionLabel={(o) => o?.label?.toString() ?? ""}
      loading={loading}
      filterOptions={filterOptions}
      value={selected}
      inputValue={text ?? ""}
      onInputChange={(e, val,reason) => {
        if (reason === "clear") {
          // Prevent Autocomplete from generating a blank option
            setText("");
            searchDebounce("");  
            return;
          }
          setText(val);
          searchDebounce(val);
        }}
      onChange={(e, val) => handleChange(e, val)}
      disabled={nonManagerDetail || disabled}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      size="small"
      sx={{
        width: 300,

        "& .MuiOutlinedInput-root": {
          borderRadius: 0,
          "& fieldset": {
            border: `1px solid ${theme.palette.artisticStroke200.main}`,
          },
        },

        // MUI handles endAdornment layout; just rotate popup chevron
        "& .MuiAutocomplete-popupIndicator": {
          padding: 0,
          marginRight: "4px",
          transition: "transform 0.3s ease",
        },
        "& .MuiAutocomplete-popupIndicatorOpen": {
          transform: "rotate(180deg)",
        },

        ...styles,
      }}
      renderOption={(optionProps, option, state) => (
        <li
          {...optionProps}
          style={{ padding: "0px 16px", display: "flex", alignItems: "center" }}
        >
          <Checkbox
            icon={icon}
            checkedIcon={checkedIcon}
            checked={state.selected}
            onClick={() => {
              const el = document.querySelector(".custom-autocomplete-scroll") as HTMLElement | null;
              if (el) lastScrollTop = el.scrollTop;
            }}
          />
          {getOptionLabel(option)}
        </li>
      )}
      renderTags={(tagValue, getTagProps) => {
        // Remove empty chips injected by MUI
        const cleanedTags = tagValue.filter(
          (item) =>
            item &&
            typeof item === "object" &&
            item.id &&
            item.label &&
            item.label.toString().trim() !== ""
        );

        return (
          <Box
            sx={{
              maxHeight: 120,
              overflowY: "auto",
              display: "flex",
              flexWrap: "wrap",
            }}
          >
            {cleanedTags.map((option, idx) => (
              <Chip
                {...getTagProps({ index: idx })}
                key={option.id ?? idx}
                label={option.label}
                size="small"
                sx={{ margin: "2px 4px" }}
              />
            ))}
          </Box>
        );
      }}

      renderInput={(params) => (
        <TextField
          {...params}
          onChange={(e) => {
            const found = columnOptions?.find((c: any) => c.field === selectedField)?.id;
            if (found && found !== selectedFieldId) setSelectedFieldId(found);
            searchDebounce(e.target.value);
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 0,
              "& fieldset": { border: `1px solid ${theme.palette.artisticStroke200.main}` },
            },
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
